import React, { useEffect, useState } from "react";
import { ImageLightbox } from "./ImageLightbox";

interface GalleryOverlayProps {
  images: string[];
  isOpen: boolean;
  onClose: () => void;
}

export function GalleryOverlay({ images, isOpen, onClose }: GalleryOverlayProps) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !lightboxImage) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, lightboxImage]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/95 overflow-y-auto"
        onClick={onClose}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="fixed top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          aria-label="Close gallery"
        >
          <svg
            className="w-8 h-8"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>

        {/* Thumbnail grid */}
        <div
          className="max-w-5xl mx-auto px-4 py-16"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-2xl font-bold text-[#a79c68] text-center mb-8">Gallery</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((src, index) => (
              <div
                key={index}
                className="aspect-square rounded-lg overflow-hidden cursor-pointer transition-transform hover:scale-105 border-2 border-[#a79c68]/30 hover:border-[#a79c68]"
                onClick={() => setLightboxImage(src)}
              >
                <img
                  src={src}
                  alt={`Gallery image ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full-size lightbox */}
      <ImageLightbox
        imageUrl={lightboxImage || ""}
        imageAlt="Gallery image"
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </>
  );
}
